<div class="fitness-calendar relative" [ngClass]="CalendarFullScreen ? 'h-full' : ''">
  <div
    class="absolute left-0 top-0 w-full h-full flex align-items-center justify-content-center z-5 transition-all transition-duration-400"
    [ngClass]="{
      'opacity-100 visible pointer-events-auto': (loading | async) === true,
      'opacity-0 invisible pointer-events-none': (loading | async) === false,
    }"
    style="background: rgba(14, 14, 14, 0.3)"
    aria-label="calendar-loader"
  >
    <p-progressSpinner
      styleClass="w-4rem h-4rem"
      strokeWidth="2"
      fill="var(--surface-ground)"
      animationDuration=".5s"
      aria-label="loading-spinner"
    />
  </div>

  <ejs-schedule
    #scheduleObj
    width="100%"
    height="100%"
    cssClass="md-custom-header"
    [selectedDate]="currentDate"
    [currentView]="currentView"
    [group]="group"
    [rowAutoHeight]="true"
    [eventSettings]="eventSettings"
    (created)="onScheduleCreated($event)"
    (eventClick)="onEventClick($event)"
    (cellClick)="onCellClick($event)"
    (actionBegin)="onActionBegin($event)"
    (actionComplete)="onActionComplete($event)"
    (eventRendered)="onEventRendered($event)"
    aria-label="calendar-component"
  >
    <e-resources>
      <!-- Define resources based on view (Trainer/Studio) -->
      @if (isTrainerView) {
        <e-resource
          field="ResourceId"
          title="Trainer"
          name="Trainers"
          [dataSource]="trainerResources"
          textField="name"
          idField="ResourceId"
          colorField="color"
          [allowMultiple]="true"
        />
      }
      @if (isStudioView) {
        <e-resource
          field="ResourceId"
          title="Studio"
          name="Studios"
          [dataSource]="studioResource"
          textField="name"
          idField="ResourceId"
          colorField="color"
          [allowMultiple]="true"
        />
      }
    </e-resources>

    <!-- Resource Header Template -->
    <ng-template #resourceHeaderTemplate let-data>
      <div
        class="md-resource-details-cont flex align-items-center"
        [attr.aria-label]="'resource-details-' + data.resourceData?.name"
      >
        <!-- Use this when Image not Present -->
        @if (isTrainerView) {
          <div>
            @if (data.resourceData?.img) {
              <p-avatar
                [image]="data.resourceData?.img"
                class="mr-2"
                [attr.aria-label]="'resource-avatar-' + data.resourceData?.name"
              />
            } @else {
              <p-avatar
                styleClass="text-sm w-2rem h-2rem mr-2"
                label="{{ data.resourceData?.name | titlecase | shortName }}"
                [attr.aria-label]="'resource-avatar-' + data.resourceData?.name"
              />
            }
          </div>
        }
        <div class="md-resource-details-name -mt-1 text-xs">
          {{ data.resourceData?.name }}
        </div>
      </div>
    </ng-template>

    <e-views>
      <!-- Use all standard views with their proper configuration -->
      <e-view option="Day" [timeScale]="timeScaleOption" />
      <e-view option="Week" [timeScale]="timeScaleOption" />
      <e-view option="WorkWeek" [timeScale]="timeScaleOption" />
      <e-view
        option="Month"
        [showWeekend]="true"
        [showWeekNumber]="true"
        [maxEventsPerRow]="1"
      />
      <e-view option="Agenda" [allowVirtualScrolling]="true" />
      <!-- Trainer and Studio views use TimelineDay with different display names -->
      <e-view
        option="TimelineDay"
        [timeScale]="timeScaleOption"
        displayName="Trainer View"
        [resourceHeaderTemplate]="resourceHeaderTemplate"
        [group]="group"
      />
      <e-view
        option="TimelineDay"
        [timeScale]="timeScaleOption"
        displayName="Studio View"
        [resourceHeaderTemplate]="resourceHeaderTemplate"
        [group]="group"
      />
    </e-views>

    <!-- Event Template -->
    <ng-template #eventSettingsTemplate let-data>
      @if (data.Type === 'appointment') {
        <div
          [ngClass]="data.CssClass"
          class="flex align-items-center p-1 line-height-1 overflow-hidden"
          [attr.aria-label]="'appointment-event-' + data.Guid"
        >
          <ng-container
            [ngTemplateOutlet]="appointmentViewInWeek"
            [ngTemplateOutletContext]="{
              data: getAppointmentFromData(data),
              event: data,
            }"
          />
        </div>
      }
      @if (data.Type === 'class') {
        <div
          [ngClass]="data.CssClass"
          class="p-1 line-height-1 overflow-hidden"
          [attr.aria-label]="'class-event-' + data.Guid"
        >
          <ng-container
            [ngTemplateOutlet]="classViewInWeek"
            [ngTemplateOutletContext]="{
              data: getClassFromData(data),
              event: data,
            }"
          />
        </div>
      }
    </ng-template>

    <!-- Agenda Template -->
    <ng-template #agendaTemplate let-data>
      <div class="md-full-event" [attr.aria-label]="'agenda-day-' + data.date">
        @for (event of data.events; track event) {
          <div>
            <div
              class="p-2 line-height-2 text-900 overflow-hidden flex align-items-center justify-content-between cursor-pointer"
              (click)="openClassAppointmentPopup(event)"
              [ngClass]="event.CssClass"
              [attr.aria-label]="'agenda-event-' + event.Guid"
            >
              <ng-container
                [ngTemplateOutlet]="
                  event.Type === 'class' ? classDetails : appointmentDetails
                "
                [ngTemplateOutletContext]="{
                  data:
                    event.Type === 'class'
                      ? getClassDetailsForAgenda(event)
                      : getAppointmentFromData(event),
                  event: event,
                }"
              />
            </div>
            <hr
              class="border-300 border-top-1 border-bottom-none border-left-none border-right-none m-0"
            />
          </div>
        }
      </div>
      <hr
        class="border-white border-top-1 border-bottom-none border-left-none border-right-none my-0"
      />
    </ng-template>

    <!-- Shared template parts -->
    <ng-template #classDetails let-classObj="data" let-event="event">
      @if (classObj) {
        <div
          class="w-7"
          [ngClass]="classObj.status === 'Cancelled' ? 'line-through' : ''"
          aria-label="class-details"
        >
          <div class="text-xs flex align-items-center">
            <strong>{{ classObj.type_name_primary_language }}</strong>
            @if (classObj.is_virtual) {
              <i
                class="fa fa-video ml-2 text-900"
                aria-label="virtual-class-indicator"
              ></i>
            }
          </div>
          <div class="text-xxs">
            <strong
              >{{ 'SCHEDULE.CALENDAR.RESERVED' | translate }}
              {{ classObj.total_booking }} / {{ classObj.max_capacity }}</strong
            >
            <strong
              >{{ 'SCHEDULE.CALENDAR.WAITLIST' | translate }}
              {{ classObj.total_waitlist_booking }} /
              {{ classObj.max_waitlist }}</strong
            >
            <br />
            <strong>{{ 'SCHEDULE.CALENDAR.TRAINER' | translate }}: </strong>
            {{ classObj.coach_name }}
            <br />
            <strong>{{ 'SCHEDULE.CALENDAR.DURATION' | translate }}: </strong
            >{{ classObj.class_duration }}
            {{ 'SCHEDULE.CALENDAR.MIN' | translate }} ({{
              event.StartTime | date: 'shortTime'
            }}
            {{ 'SCHEDULE.CALENDAR.TO' | translate }}
            {{ event.EndTime | date: 'shortTime' }})
          </div>
        </div>
      }
      @if (classObj && classObj.class_clients) {
        <div
          class="w-5 flex align-items-center justify-content-end overflow-hidden"
          aria-label="class-participants"
        >
          @for (client of classObj.class_clients | slice: 0 : 3; track client) {
            @if (client.image) {
              <p-avatar
                [image]="client.image"
                styleClass="mr-2 w-2rem h-2rem"
                shape="circle"
                [attr.aria-label]="'participant-avatar-' + client.full_name"
              />
            } @else {
              <p-avatar
                styleClass="text-sm w-2rem h-2rem mr-2"
                [label]="client.full_name | titlecase | shortName"
                shape="circle"
                [attr.aria-label]="'participant-avatar-' + client.full_name"
              />
            }
          }
          @if (classObj.class_clients.length > 3) {
            <span class="text-xs text-600 ml-1">
              +{{ classObj.class_clients.length - 3 }} more</span
            >
          }
        </div>
      }
    </ng-template>

    <ng-template
      #appointmentDetails
      let-appointmentObj="data"
      let-event="event"
    >
      @if (appointmentObj) {
        <div
          class="flex align-items-center"
          [ngClass]="
            appointmentObj.status === 'Cancelled' ? 'line-through' : ''
          "
          aria-label="appointment-details"
        >
          @if (
            appointmentObj.clients.length > 0 && appointmentObj.clients[0].image
          ) {
            <p-avatar
              [image]="appointmentObj.clients[0].image"
              styleClass="mr-1 w-2rem h-2rem border-round"
              [attr.aria-label]="
                'client-avatar-' + appointmentObj.clients[0].full_name
              "
            />
          } @else {
            <p-avatar
              styleClass="text-sm w-2rem h-2rem mr-2"
              [label]="
                appointmentObj.clients.length > 0
                  ? (appointmentObj.clients[0].full_name
                    | titlecase
                    | shortName)
                  : ''
              "
              shape="circle"
              [attr.aria-label]="
                'client-avatar-' + appointmentObj.clients[0].full_name
              "
            />
          }
          <div>
            <div class="text-xs">
              <strong>{{ appointmentObj.type_name_primary_language }}</strong>
              {{ 'SCHEDULE.CALENDAR.WITH' | translate }}
              {{
                appointmentObj.clients.length > 0
                  ? appointmentObj.clients[0].full_name
                  : ''
              }}
            </div>
            <div class="text-xxs">
              {{ event.StartTime | date: 'shortTime' }} -
              {{ event.EndTime | date: 'shortTime' }}
              @if (appointmentObj.coach_name) {
                <span>({{ appointmentObj.coach_name }})</span>
              }
            </div>
          </div>
        </div>
      }
    </ng-template>

    <ng-template #classViewInWeek let-class="data" let-event="event">
      <div
        class="text-xs flex align-items-center"
        [ngClass]="class.status === 'Cancelled' ? 'line-through' : ''"
        aria-label="class-title"
      >
        <strong>{{ class.type_name_primary_language }}</strong>
        @if (class.is_virtual) {
          <i
            class="fa fa-video ml-2 text-900"
            aria-label="virtual-class-indicator"
          ></i>
        }
      </div>
      <div
        class="text-xxs"
        [ngClass]="class.status === 'Cancelled' ? 'line-through' : ''"
        aria-label="class-details"
      >
        @if (!isTrainerView && !isStudioView) {
          <span>
            {{ 'SCHEDULE.CALENDAR.RES' | translate }}
            <strong
              >{{ class.total_booking }} / {{ class.max_capacity }}</strong
            >
            {{ 'SCHEDULE.CALENDAR.WAIT' | translate }}
            <strong
              >{{ class.total_waitlist_booking }} /
              {{ class.max_waitlist }}</strong
            ></span
          >
        }
        @if (!isTrainerView && !isStudioView) {
          <br />
        }
        {{ class.coach_name }} <br />
        {{ class.class_duration }} {{ 'SCHEDULE.CALENDAR.MIN' | translate }}
      </div>
    </ng-template>

    <ng-template
      #appointmentViewInWeek
      let-appointment="data"
      let-event="event"
    >
      @if (!isTrainerView && !isStudioView) {
        <div>
          @if (
            appointment.clients?.length > 0 && appointment.clients[0].image
          ) {
            <p-avatar
              [image]="appointment.clients[0].image"
              styleClass="mr-1 w-2rem h-2rem border-round"
              [attr.aria-label]="
                'client-avatar-' + appointment.clients[0].full_name
              "
            />
          } @else {
            <p-avatar
              styleClass="text-sm w-2rem h-2rem mr-2"
              [label]="
                appointment.clients?.length > 0
                  ? (appointment.clients[0].full_name | titlecase | shortName)
                  : ''
              "
              shape="circle"
              [attr.aria-label]="
                'client-avatar-' + appointment.clients[0].full_name
              "
            />
          }
        </div>
      }
      <div
        [ngClass]="appointment.status === 'Cancelled' ? 'line-through' : ''"
        aria-label="appointment-details"
      >
        <div class="text-xs">
          <strong>{{ appointment.type_name_primary_language }}</strong>
          @if (appointment.clients?.length > 0) {
            <span>
              {{ 'SCHEDULE.CALENDAR.WITH' | translate }}
              {{ appointment.clients[0].full_name }}</span
            >
          }
        </div>
        <div class="text-xxs">
          {{ event.StartTime | date: 'shortTime' }} -
          {{ event.EndTime | date: 'shortTime' }}
        </div>
      </div>
    </ng-template>
  </ejs-schedule>
</div>
